import { useEffect, useState } from 'react'
import { Accordion, AccordionItem, Button, Input } from '@nextui-org/react'
import CompanyInfo from '../components/company-info'
import CompanyLegal from '../components/company-legal'
import CompanyFinancial from '../components/company-financial'
import CompanyHR from '../components/company-hr'
import CompanyTechnicalCapabilities from '../components/company-technical'
import CompanyReferences from '../components/company-references'
import CompanyDocTemplates from '../components/company-docs'
import CompanyTenderExperience from '../components/company-tenders-experience'
import CompanyInsurance from '../components/company-insureance'
import CompanyESG from '../components/company-esg'
import api from 'src/lib/api'
import RSelect from 'react-select'
import { Icon } from '@iconify/react'
import toast from 'react-hot-toast'

export default function OnboardingFormWizard() {
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({})
  const handleChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }
  const [openAccordion, setOpenAccordion] = useState<string>('0')
  const [userUUID, setUserUUID] = useState<string>('')
  const [users, setUsers] = useState<
    { id: number; uuid: string; firstname: string; lastname: string; email: string }[]
  >([])

  const getUsers = async () => {
    const res = await api.get('/admin/users')
    setUsers(res.data ?? [])
  }
  useEffect(() => {
    getUsers()
  }, [])
  const onNext = () => {
    console.log('nex tpressed')
    setOpenAccordion((prev) => {
      const step = parseInt(prev) + 1;
      const el = document.getElementById(`accordion-${step}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return step.toString()
    })
  }
  const onPrevious = () => {
    setOpenAccordion((prev) => {
      const step = Math.max(parseInt(prev) - 1, 0)
      const el = document.getElementById(`accordion-${step}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return step.toString()
    })
  }
  const [loading, setLoading] = useState<boolean>(false)
  const update = async () => {
    setLoading(true)
    await toast.promise(api.post(`/admin/user/${userUUID}`, formData), {
      loading: 'Updating company profile',
      success: 'Company profile has been updated',
      error: 'There was unexpected error updating your company profile.',
    })
    setFormData({})
    setUserUUID('')
    setLoading(false)
  }
  const [firstname, setFirstname] = useState<string>('')
  const [lastname, setLastname] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const createUserAndGoNext = async () => {
    const newUser = await toast.promise(
      api.post('/admin/users', {
        firstname,
        lastname,
        email,
        password,
      }),
      {
        success: 'Client has been created',
        error: 'Unable to create user',
        loading: 'Creating user account..',
      },
    )
    if (newUser.data?.uuid) {
      const index = users.findIndex((item) => item.uuid === newUser.data?.uuid)
      if (index === -1) {
        setUsers((prev) => [...prev, newUser.data])
      }
      setUserUUID(newUser.data?.uuid)
    }
  }
  return (
    <div className="flex flex-col w-full max-w-screen-xl min-h-screen p-3 overflow-y-auto">
      <div className="flex w-full pt-8 pb-6 px-2 2xl:px-8">
        <header className="flex w-full justify-between">
          <div className="flex flex-1 flex-col">
            <h1 className="text-xl font-bold text-default-900 lg:text-3xl">Manage Company Profile</h1>
          </div>
          <div className="min-w-[350px] bg-white">
            <RSelect
              className={'flex flex-1 w-full bg-white w-[500px]'}
              onChange={(option) => {
                setUserUUID(option?.value)
              }}
              placeholder={`Assign tenders to client`}
              options={users.map((client) => ({
                value: client.uuid,
                label: [client.firstname ?? '', client.lastname ?? '', ' - ', client.email].join(' ').trim(),
              }))}
              styles={{
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#3D969E' : '#fff',
                  color: state.isFocused ? '#fff' : '#333',
                  padding: '10px 12px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  color: '#333',
                  fontWeight: '500',
                }),
                menuList: (provided) => ({
                  ...provided,
                  padding: '0',
                }),
                control: (base, state) => ({
                  ...base,
                  width: '100%', // Ensure the control spans the full width
                  minWidth: '100%',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  border: state.isFocused ? '1px solid #000' : '1px solid #E0E0E0',
                  boxShadow: state.isFocused ? '0 0 4px rgba(0, 114, 245, 0.3)' : 'none',
                  padding: '4px',
                  transition: 'border 0.2s ease, box-shadow 0.2s ease',
                }),
                menu: (base) => ({
                  ...base,
                  width: '100%', // Dropdown menu spans full width
                  minWidth: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                }),
                menuPortal: (base) => ({ ...base, zIndex: 99999 }),
              }}
              menuPortalTarget={window.document.querySelector('body')}
            />
          </div>
        </header>
      </div>

      <Accordion
        defaultSelectedKeys={['0']}
        variant="splitted"
        itemClasses={{ title: 'font-semibold', base: 'px-7 py-4' }}
        selectedKeys={[openAccordion]}
        onSelectionChange={(keys: any) => {
          const iterator = keys.values().next().value
          if (iterator) {
            setOpenAccordion(iterator)
          }
        }}
      >
        <AccordionItem id="accordion-0" key="0" aria-label="Company User Profile" title="Create User Profile">
          <div className="w-full flex gap-4 flex-col">
            <div className="flex flex-row gap-6">
              <Input
                isRequired
                label="Firstname"
                variant="bordered"
                placeholder="John"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
              />
              <Input
                isRequired
                label="Lastname"
                variant="bordered"
                placeholder="Doe"
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
              />
            </div>
            <div className="flex flex-row gap-6">
              <Input
                isRequired
                label="Email"
                variant="bordered"
                placeholder="john.doe@powerdocs.so"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                isRequired
                label="Password"
                variant="bordered"
                placeholder="******"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-row justify-between mt-6">
            <div />
            <Button color="secondary" onPress={createUserAndGoNext} endContent={<Icon icon="lucide:chevron-right" />}>
              Next step
            </Button>
          </div>
        </AccordionItem>
        <AccordionItem id="accordion-1" key="1" aria-label="Company Information" title="1. Company Information">
          <CompanyInfo formData={formData} handleChange={handleChange} />
          <div className="flex flex-row justify-between mt-6">
            <Button
              variant="bordered"
              color="default"
              onPress={onPrevious}
              startContent={<Icon icon="lucide:chevron-left" />}
            >
              Create User
            </Button>
            <Button color="secondary" onPress={onNext} endContent={<Icon icon="lucide:chevron-right" />}>
              Next step
            </Button>
          </div>
        </AccordionItem>
        <AccordionItem id="accordion-2" key="2" aria-label="Legal & Compliance" title="2. Legal & Compliance">
          <CompanyLegal formData={formData} handleChange={handleChange} userUUID={userUUID} />
          <div className="flex flex-row justify-between mt-6">
            <Button variant="bordered" color="default" onPress={onPrevious}>
              Back
            </Button>
            <Button color="secondary" onPress={onNext} endContent={<Icon icon="lucide:chevron-right" />}>
              Next step
            </Button>
          </div>
        </AccordionItem>
        <AccordionItem key="3" aria-label="Financials" title="3. Financials">
          <CompanyFinancial formData={formData} handleChange={handleChange} userUUID={userUUID} />
          <div className="flex flex-row justify-between mt-6">
            <Button variant="bordered" color="default" onPress={onPrevious}>
              Back
            </Button>
            <Button color="secondary" onPress={onNext} endContent={<Icon icon="lucide:chevron-right" />}>
              Next step
            </Button>
          </div>
        </AccordionItem>
        <AccordionItem key="4" aria-label="Human Resources" title="4. Human Resources">
          <CompanyHR formData={formData} handleChange={handleChange} userUUID={userUUID} />
          <div className="flex flex-row justify-between mt-6">
            <Button variant="bordered" color="default" onPress={onPrevious}>
              Back
            </Button>
            <Button color="secondary" onPress={onNext} endContent={<Icon icon="lucide:chevron-right" />}>
              Next step
            </Button>
          </div>
        </AccordionItem>
        <AccordionItem key="5" aria-label="Technical Capabilities" title="5. Technical Capabilities">
          <CompanyTechnicalCapabilities formData={formData} handleChange={handleChange} userUUID={userUUID} />
          <div className="flex flex-row justify-between mt-6">
            <Button variant="bordered" color="default" onPress={onPrevious}>
              Back
            </Button>
            <Button color="secondary" onPress={onNext} endContent={<Icon icon="lucide:chevron-right" />}>
              Next step
            </Button>
          </div>
        </AccordionItem>
        <AccordionItem key="6" aria-label="References & Projects" title="6. References & Projects">
          <CompanyReferences formData={formData} handleChange={handleChange} userUUID={userUUID} />
          <div className="flex flex-row justify-between mt-6">
            <Button variant="bordered" color="default" onPress={onPrevious}>
              Back
            </Button>
            <Button color="secondary" onPress={onNext} endContent={<Icon icon="lucide:chevron-right" />}>
              Next step
            </Button>
          </div>
        </AccordionItem>
        <AccordionItem key="7" aria-label="Documentation & Templates" title="7. Documentation & Templates">
          <CompanyDocTemplates formData={formData} handleChange={handleChange} userUUID={userUUID} />
          <div className="flex flex-row justify-between mt-6">
            <Button variant="bordered" color="default" onPress={onPrevious}>
              Back
            </Button>
            <Button color="secondary" onPress={onNext} endContent={<Icon icon="lucide:chevron-right" />}>
              Next step
            </Button>
          </div>
        </AccordionItem>
        <AccordionItem key="8" aria-label="Tender-Specific Experience" title="8. Tender-Specific Experience">
          <CompanyTenderExperience formData={formData} handleChange={handleChange} userUUID={userUUID} />
          <div className="flex flex-row justify-between mt-6">
            <Button variant="bordered" color="default" onPress={onPrevious}>
              Back
            </Button>
            <Button color="secondary" onPress={onNext} endContent={<Icon icon="lucide:chevron-right" />}>
              Next step
            </Button>
          </div>
        </AccordionItem>
        <AccordionItem key="9" aria-label="Insurance & Risk Management" title="9. Insurance & Risk Management">
          <CompanyInsurance formData={formData} handleChange={handleChange} userUUID={userUUID} />
          <div className="flex flex-row justify-between mt-6">
            <Button variant="bordered" color="default" onPress={onPrevious}>
              Back
            </Button>
            <Button color="secondary" onPress={onNext} endContent={<Icon icon="lucide:chevron-right" />}>
              Next step
            </Button>
          </div>
        </AccordionItem>
        <AccordionItem key="10" aria-label="ESG & CSR" title="10. ESG & CSR">
          <CompanyESG formData={formData} handleChange={handleChange} userUUID={userUUID} />
          <div className="flex flex-row justify-between mt-6">
            <Button variant="bordered" color="default" onPress={onPrevious}>
              Back
            </Button>
            <Button color="secondary" onPress={update} endContent={<Icon icon="lucide:chevron-right" />}>
              Save
            </Button>
          </div>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
